import React, { useState } from "react";
import {
  STEWARD_ENTITY,
  STEWARD_ENTITY_ADDRESS,
  STEWARD_BANK,
  STEWARD_BANK_ADDRESS,
  STEWARD_SWIFT,
  STEWARD_SWIFT_INTERMEDIARY,
  STEWARD_ACCOUNT_TYPE,
  STEWARD_ABA,
  STEWARD_ABA_FALLBACK,
  STEWARD_ACCOUNT_NUMBER,
  STEWARD_WIRE_PDF_PATH,
  STEWARD_MEMO_PREFIX,
} from "@shared/campaign";

interface Row {
  label: string;
  value: string;
  mono?: boolean;
}

function FieldList({ rows }: { rows: Row[] }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
      {rows.map((r) => (
        <React.Fragment key={r.label}>
          <dt className="font-mono text-[10px] uppercase tracking-widest text-ink-3 sm:pt-1">
            {r.label}
          </dt>
          <dd
            className={
              r.mono
                ? "font-mono text-ink text-sm break-all"
                : "text-ink leading-snug"
            }
          >
            {r.value}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export interface WireDetailsPanelProps {
  /**
   * If the panel is rendered for a specific Wish List item, the memo code
   * suffix (e.g. "WISH-DGX-SPARK") is appended to the prefix. If undefined,
   * the panel uses the generic "DPF-GENERAL" memo.
   */
  itemId?: string;
  /** The notify-the-Foundation link target. Defaults to the contact anchor. */
  notifyHref?: string;
}

/**
 * WireDetailsPanel — surfaces the Mind Hack, Inc. stewardship-account wire
 * instructions. Two layers:
 *   1. Always-visible (when the parent <details> is expanded): entity,
 *      bank, SWIFT, account type, memo code.
 *   2. Click-to-reveal (no auth): ABA routing + account number. Mercury
 *      ACH debit-block is enabled, mitigating the standard "scrapeable
 *      numbers" risk; the same numbers also live in the gated PDF.
 *
 * Use inside a parent <details> element so a single page can host one
 * panel per Wish List item without expanding the page on first paint.
 */
export function WireDetailsPanel({ itemId, notifyHref }: WireDetailsPanelProps) {
  const [showNumbers, setShowNumbers] = useState(false);

  const memoCode = itemId
    ? `${STEWARD_MEMO_PREFIX}WISH-${itemId.toUpperCase()}`
    : `${STEWARD_MEMO_PREFIX}GENERAL`;

  const beneficiaryRows: Row[] = [
    { label: "Beneficiary", value: STEWARD_ENTITY },
    { label: "Address", value: STEWARD_ENTITY_ADDRESS },
    { label: "Account type", value: STEWARD_ACCOUNT_TYPE },
    { label: "Memo code", value: memoCode, mono: true },
  ];

  const bankRows: Row[] = [
    { label: "Receiving bank", value: STEWARD_BANK },
    { label: "Bank address", value: STEWARD_BANK_ADDRESS },
    { label: "SWIFT / BIC", value: STEWARD_SWIFT, mono: true },
    {
      label: "Intermediary SWIFT",
      value: `${STEWARD_SWIFT_INTERMEDIARY} (JPMorgan Chase)`,
      mono: true,
    },
  ];

  const numberRows: Row[] = [
    { label: "ABA routing", value: STEWARD_ABA, mono: true },
    {
      label: "ABA (fallback)",
      value: `${STEWARD_ABA_FALLBACK} — if your bank does not recognise the primary`,
      mono: true,
    },
    { label: "Account number", value: STEWARD_ACCOUNT_NUMBER, mono: true },
  ];

  const resolvedNotifyHref = notifyHref ?? "#contact";

  return (
    <div className="border border-hairline bg-paper p-6 md:p-8 space-y-8">
      <section aria-labelledby={`wire-beneficiary-${memoCode}`}>
        <h4
          id={`wire-beneficiary-${memoCode}`}
          className="font-mono text-[10px] uppercase tracking-widest text-claude font-bold mb-3"
        >
          Beneficiary
        </h4>
        <FieldList rows={beneficiaryRows} />
      </section>

      <section
        aria-labelledby={`wire-bank-${memoCode}`}
        className="border-t border-hairline pt-6"
      >
        <h4
          id={`wire-bank-${memoCode}`}
          className="font-mono text-[10px] uppercase tracking-widest text-claude font-bold mb-3"
        >
          Receiving bank
        </h4>
        <FieldList rows={bankRows} />
      </section>

      <section
        aria-labelledby={`wire-numbers-${memoCode}`}
        className="border-t border-hairline pt-6"
      >
        <h4
          id={`wire-numbers-${memoCode}`}
          className="font-mono text-[10px] uppercase tracking-widest text-claude font-bold mb-3"
        >
          Routing &amp; account number
        </h4>
        {showNumbers ? (
          <>
            <FieldList rows={numberRows} />
            <p className="text-xs font-mono text-ink-3 mt-4 leading-relaxed">
              These numbers also appear in the downloadable PDF below. ACH
              debit-block is enabled on the receiving account.
            </p>
            <button
              type="button"
              onClick={() => setShowNumbers(false)}
              className="mt-4 text-[10px] font-mono uppercase tracking-widest text-ink-3 underline decoration-hairline hover:decoration-ink"
            >
              Hide numbers
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setShowNumbers(true)}
            className="text-xs uppercase tracking-widest font-semibold border border-ink text-ink px-5 py-2.5 hover:bg-ink hover:text-paper transition-colors"
          >
            Reveal account details
          </button>
        )}
      </section>

      <section className="border-t border-hairline pt-6 flex flex-wrap items-center gap-4">
        <a
          href={STEWARD_WIRE_PDF_PATH}
          download
          className="text-xs uppercase tracking-widest font-semibold bg-ink text-paper px-5 py-2.5 hover:bg-ink-2 transition-colors"
        >
          Download PDF
        </a>
        <a
          href={resolvedNotifyHref}
          className="text-xs uppercase tracking-widest font-semibold border border-ink text-ink px-5 py-2.5 hover:bg-paper-2 transition-colors"
        >
          Notify us your gift is on the way
        </a>
        <p className="text-[11px] font-mono text-ink-3 leading-relaxed basis-full">
          This page does not collect payment information. Your wire is sent
          via your own bank.
        </p>
      </section>
    </div>
  );
}
